import React, { useContext, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery } from '@apollo/react-hooks';
import { Alert, AlertTitle, Collapse } from '@mui/material';
import Markdown from 'Components/Markdown/Markdown';
import UserDetailsContext from 'Context/userDetailsContext';
import { readCookie, saveCookie } from 'Utils/cookies';
import moment from 'moment';
import {
  AnnouncementPersistenceTypes,
  AnnouncementCookieTypes,
} from 'Types/announcements';
import { getAnnouncements } from './gqlQuery';

export const AnnouncementsContainer = () => {
  const userDetails = useContext(UserDetailsContext);
  const { instId } = userDetails;

  const { data: announcementsData } = useQuery(getAnnouncements, {
    variables: {
      instId,
    },
  });

  const { announcements } = announcementsData?.institutions || [];

  const [closedAnnouncements, setClosedAnnouncements] = useState({});

  const cookieOnceData = readCookie(AnnouncementCookieTypes.ONCE);

  const cookieSessionData = readCookie(AnnouncementCookieTypes.SESSION);

  const cookieData = `${cookieOnceData + cookieSessionData}`;
  const closedIds = cookieData
    ? cookieData.split(',').filter((item) => item.length !== 0)
    : [];

  const cookieCreation = (id: string, persistence: string = null) => {
    if (persistence === AnnouncementPersistenceTypes.ONCE) {
      saveCookie(
        AnnouncementCookieTypes.ONCE,
        `${cookieOnceData || ''}${id},`,
        120
      );
    }
    if (persistence === AnnouncementPersistenceTypes.SESSION) {
      saveCookie(
        AnnouncementCookieTypes.SESSION,
        `${cookieSessionData || ''}${id},`
      );
    }
  };

  const isMarkdown = (text: string) => {
    const markdownPatterns = [
      /\[.*\]\(.*\)/, // links
      /!\[.*\]\(.*\)/, // images
      /^# .*/, // h1 header
      /^\* .*$/, // unordered list
      /^\d+\. .*$/, // ordered list
      /\*\*.*\*\*/, // bold text
      /\*.*\*/, // italic text
    ];
    return markdownPatterns.some((pattern) => pattern.test(text));
  };

  const closeAnnouncement = (id: string, persistence: string = null) => {
    cookieCreation(id, persistence);
    const addAnnouncement = { [id]: true };
    setClosedAnnouncements((allClosedAnnouncements) => ({
      ...allClosedAnnouncements,
      ...addAnnouncement,
    }));
  };

  const renderAnnouncements = () => {
    const today = new Date();
    const filteredAnnouncements = announcements.filter(
      (announcement: any) => !closedIds.includes(`${announcement.id}`)
    );
    return filteredAnnouncements.map(
      (announcement: any) =>
        (moment(today).isBetween(
          moment.utc(announcement.start_date),
          moment.utc(announcement.end_date)
        ) ||
          (!announcement.start_date && !announcement.end_date)) && (
          <Collapse
            key={announcement.id}
            in={!closedAnnouncements[announcement.id]}
          >
            <Alert
              sx={{ justifyContent: 'center', position: 'relative', zIndex: 5 }}
              severity={announcement.severity}
              onClose={() => {
                closeAnnouncement(announcement.id, announcement.persistence);
              }}
            >
              <AlertTitle
                sx={{
                  fontFamily: "'Museo Sans', sans-serif",
                  fontWeight: 500,
                  margin: 0,
                }}
              >
                {isMarkdown(announcement.title) ? (
                  <Markdown text={announcement.title} />
                ) : (
                  announcement.title
                )}
              </AlertTitle>
              {isMarkdown(announcement.subtext) ? (
                <Markdown text={announcement.subtext} />
              ) : (
                announcement.subtext
              )}
            </Alert>
          </Collapse>
        )
    );
  };

  return <>{announcements && renderAnnouncements()}</>;
};

export default AnnouncementsContainer;
